import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import type { BoardTimelineData, BoardTimelineEvent } from '../../types';
import { BOARD_TIMELINE_MAX_EVENTS } from './boardConstants';
import './TimelineBuilderModal.css';

interface TimelineBuilderModalProps {
  open: boolean;
  initial?: BoardTimelineData;
  onClose: () => void;
  onSubmit: (data: BoardTimelineData) => void;
}

function genEventId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

const defaultEvent = (): BoardTimelineEvent => ({
  id: genEventId(),
  label: '',
  duration: 1,
  unit: 'weeks',
});

const TimelineBuilderModal: React.FC<TimelineBuilderModalProps> = ({
  open,
  initial,
  onClose,
  onSubmit,
}) => {
  const [title, setTitle] = useState('');
  const [events, setEvents] = useState<BoardTimelineEvent[]>([defaultEvent()]);

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || '');
    setEvents(
      initial?.events?.length
        ? initial.events.map((e) => ({ ...e }))
        : [defaultEvent()],
    );
  }, [open, initial]);

  if (!open) return null;

  const updateEvent = (id: string, patch: Partial<BoardTimelineEvent>) => {
    setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  const addEvent = () => {
    if (events.length >= BOARD_TIMELINE_MAX_EVENTS) return;
    setEvents((prev) => [...prev, defaultEvent()]);
  };

  const removeEvent = (id: string) => {
    setEvents((prev) => (prev.length <= 1 ? prev : prev.filter((e) => e.id !== id)));
  };

  const handleSubmit = () => {
    const cleaned = events
      .map((e) => ({
        ...e,
        label: e.label.trim() || 'Event',
        duration: Math.max(1, Math.round(Number(e.duration) || 1)),
      }))
      .filter((e) => e.duration > 0);
    if (cleaned.length === 0) return;
    onSubmit({ title: title.trim() || undefined, events: cleaned });
    onClose();
  };

  return (
    <div className="board-timeline-modal-backdrop" onMouseDown={onClose}>
      <div
        className="board-timeline-modal"
        role="dialog"
        aria-labelledby="timeline-builder-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="board-timeline-modal-header">
          <h2 id="timeline-builder-title">Quick Timeline Builder</h2>
          <button type="button" className="board-timeline-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <p className="board-timeline-modal-desc">
          Add up to {BOARD_TIMELINE_MAX_EVENTS} sequential phases. Durations are relative — the chart shows proportional lengths.
        </p>

        <label className="board-timeline-field">
          <span>Title (optional)</span>
          <input
            type="text"
            value={title}
            placeholder="e.g. Concept → Production"
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>

        <div className="board-timeline-events">
          {events.map((evt, idx) => (
            <div key={evt.id} className="board-timeline-event-row">
              <span className="board-timeline-event-num">{idx + 1}</span>
              <input
                type="text"
                className="board-timeline-event-label"
                placeholder="Phase name"
                value={evt.label}
                onChange={(e) => updateEvent(evt.id, { label: e.target.value })}
              />
              <input
                type="number"
                className="board-timeline-event-duration"
                min={1}
                value={evt.duration}
                onChange={(e) => updateEvent(evt.id, { duration: Number(e.target.value) })}
              />
              <select
                className="board-timeline-event-unit"
                value={evt.unit}
                onChange={(e) => updateEvent(evt.id, { unit: e.target.value as 'days' | 'weeks' })}
              >
                <option value="days">days</option>
                <option value="weeks">weeks</option>
              </select>
              <button
                type="button"
                className="board-timeline-event-remove"
                title="Remove event"
                disabled={events.length <= 1}
                onClick={() => removeEvent(evt.id)}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="board-timeline-add-event"
          disabled={events.length >= BOARD_TIMELINE_MAX_EVENTS}
          onClick={addEvent}
        >
          <Plus size={16} />
          Add event
          {events.length >= BOARD_TIMELINE_MAX_EVENTS && ` (max ${BOARD_TIMELINE_MAX_EVENTS})`}
        </button>

        <div className="board-timeline-modal-actions">
          <button type="button" className="board-timeline-cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="board-timeline-submit" onClick={handleSubmit}>
            {initial ? 'Update timeline' : 'Add to board'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimelineBuilderModal;
